import { service } from '@ember/service'
import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { t } from 'ember-intl'
import { SearchBox } from 'teacher-ui-client/components/search-box'
import type { AssignmentOption } from 'teacher-ui-client/components/assignments-admin/assignment-form'
import { UiButtonLink } from 'teacher-ui-client/components/ui-button/link'
import config from 'teacher-ui-client/config/environment'
import type { Log } from '@blakeelearning/log'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import type Owner from '@ember/owner'
import { join } from 'teacher-ui-client/utils/uri'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { trackedFunction } from 'reactiveweb/function'

// EWI is an acronym for Exciting Writing Ideas, a precinct in Writing Legends

interface EWI {
  activity_id: string
  grade_position: number
  sort_published_date_desc: string
  sort_ranking_asc: number
  title: string
}

export interface EWIData {
  items: EWI[]
}

interface Signature {
  Args: {
    selectAssignmentAction: (option?: AssignmentOption) => void
    selectedAssignmentOption?: AssignmentOption
  }
}

function getEWIPreviewLink(ewiId: string) {
  return join(config.readingEggsBaseUrl, 'writing/teacher_portal/exciting-writing-ideas', ewiId).href
}
/**
 * A component that displays a list dropdown for Writing Legends' Exiciting Writing Ideas (EWIs)
 */
export class ActivitySelectorEWIList extends Component<Signature> {
  @service log!: Log

  @tracked searchValue?: Nullable<string> = ''

  @queryParam('assignments/product/create') writingCode?: string

  @queryParam('assignments/product/create') ewiCode?: string

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args)
  }

  loadEWIs = trackedFunction(this, async () => {
    const url = join(config.lessonsApiV1Url, 'writing_legends/exciting_writing_ideas').href
    const ewiData = await this.fetchData<EWIData>(url)
    this.preSelectEwi(ewiData)
    return ewiData
  })

  async fetchData<R>(url: string): Promise<R | undefined> {
    try {
      const response = await fetch(url)

      if (response.ok) {
        return (await response.json()) as R
      } else {
        // Some unknown error. Log it.
        this.log.error(`Failed to fetch exciting writing ideas list; Reason: (${response.status})`, url)
      }
    } catch (e: unknown) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        // Fetch aborted by user action, eg closed tab, stop button, network outage. Do nothing.
      } else {
        this.log.error(`Failed to fetch exciting writing ideas list; Reason: (error thrown)`, e ?? {}, url)
      }
    }
  }

  preSelectEwi = (ewiData?: EWIData) => {
    // if EWI code (activity_id) is provided in the query params, pre-select the EWI
    const writingCode = this.ewiCode || this.writingCode
    if (!writingCode) return

    const preSelectedEwi = ewiData?.items.find((ewi) => ewi.activity_id === writingCode)
    if (preSelectedEwi) {
      this.selectEWI(preSelectedEwi)
      this.searchValue = preSelectedEwi.title
    }
  }

  get filteredEWIs() {
    return this.loadEWIs.value?.items.filter((ewi) =>
      ewi.title.toLowerCase().includes(this.searchValue?.toLowerCase() ?? ''),
    )
  }

  // Sets the selected ewi in the component, and sends the model to the `selectAssignmentAction`
  selectEWI = (ewi: Nullable<EWI>) => {
    if (!ewi) {
      this.args.selectAssignmentAction()
      return
    }
    this.args.selectAssignmentAction({
      id: ewi.activity_id,
      label: ewi.title,
      assignmentData: {
        assignment_id: ewi.activity_id,
        assignment_type: 'ewi',
        wl_ewi_title: ewi.title,
      },
    })
  }

  findEWIByTitle = (newSearchTerm: string | null | undefined) => {
    this.searchValue = newSearchTerm
    if (!newSearchTerm) this.selectEWI(null)
  }

  get selectedEWI() {
    const { selectedAssignmentOption } = this.args
    return selectedAssignmentOption?.assignmentData.assignment_id
  }

  resetSearch = () => {
    this.searchValue = ''
  }

  <template>
    <div class="mb-4 px-3 md:w-full lg:w-1/2 lg:min-w-fit">
      <label class="mb-1">
        {{t "assignments.create.writingAssignments.ewis.title"}}
      </label>
      <div class="flex gap-4">
        {{#if this.loadEWIs.value}}
          <SearchBox
            class="w-full"
            @value={{this.searchValue}}
            @placeholder={{t "assignments.create.writingAssignments.searchPlaceholder"}}
            @updateAction={{this.findEWIByTitle}}
            @selectOptionAction={{this.selectEWI}}
            @autocomplete={{true}}
            @autocompleteOptions={{this.filteredEWIs}}
            as |ewi|
          >
            <div class="space-y-3 font-normal">
              {{ewi.title}}
            </div>
          </SearchBox>
          {{#if this.selectedEWI}}
            <UiButtonLink
              href={{getEWIPreviewLink this.selectedEWI}}
              target="_blank"
              rel="noopener noreferrer"
              class="whitespace-nowrap"
            >
              {{t "assignments.create.writingAssignments.preview"}}<PhosphorIcon
                @icon="phosphor-arrow-square-out-bold"
                class="ml-2"
              />
            </UiButtonLink>
          {{/if}}
        {{else}}
          <span class="text-dusty-black-200 italic">{{t
              "assignments.create.writingAssignments.ewis.loadingMessage"
            }}</span>
        {{/if}}
      </div>
    </div>
  </template>
}
